<template>
	<dropdown-base
    ref="dropdownBaseWrapper"
    dropdownType="currency"
	:params="params"
    :options="options"
	:dropdownIsOpen="dropdownIsOpen"
	:ocultDropArrowIfOnlyOneOption="params && !!params.ocultDropArrowIfOnlyOneOption && options.length === 1"
	@toggleDropdown="dropdownIsOpen = !dropdownIsOpen"
	@closeDropdown="closeDropdown"
    @search="dropdownOptions = $event">
		<!-- Header slot -->
		<template v-slot:header>
			<div class="header"
            :class="{
                'error-state': params.errorState,
                'cursor-pointer': options.length > 1,
                'active': dropdownIsOpen
            }">
                <div class="header-content">
                    <!-- Currency Icon -->
                    <icon-component class="icon" v-if="selectedCurrency && selectedCurrency.icon"/>
                    <!-- Placeholder -->
                    <span
                    v-if="params.placeholder && (!displayDescription.length || !displayCurrency.length)"
                    class="placeholder">
                        {{ params.placeholder }}
                    </span>
                    <!-- Text -->
                    <span class="value">
                        {{params.displayDescriptionOnHeader ? displayDescription : displayCurrency}}
                    </span>
                </div>
			</div>
		</template>
		<!-- Body slot -->
		<template v-slot:body>
            <div v-if="dropdownOptions.length" class="dropdown-options">
                <dropdown-row
                v-for="row in dropdownOptions"
                :key="row.id"
                :selectedOption="selectedOption"
                :option="row"
                @clicked="[emit('clicked', $event), closeDropdown()]"/>
            </div>
		</template>
	</dropdown-base>
</template>

<script setup>
/* Vue */
import { ref, computed, defineAsyncComponent, watch } from 'vue'
/* i18n */
import { useI18n } from 'vue-i18n'
/* Components */
import DropdownBase from '@/components/generic/dropdowns/Base'
import DropdownRow from '@/components/generic/dropdowns/currency/Row'

/* Emmitters */
const emit = defineEmits(['clicked'])

/** props received
@params { Object }
	- labelText { String}
    - placeholder { String }
	- mobileTitle {String }
	- mandatory { Boolean }
	- errorState { Boolean }
	- errorMessage { String }
	- bottomMessage { String }
	- displayDescriptionOnHeader { Boolean }
	- ocultDropArrowIfOnlyOneOption {Boolean} - option to trigger our drop to hide arrow when only one option is available
    - withSearch { Boolean }
	- searchParams { Object }
@selectedOption { String }
@options { Array } each item is an Object
	- id { String, Number }
	- currency { String }
	- description { String }
	- icon { String }
*/
const props = defineProps({
	params: Object,
	selectedOption: [String, Number, Boolean],
	options: Array
})

const { t } = useI18n()
const dropdownIsOpen = ref(false)
const dropdownOptions = ref(props.options)
const dropdownBaseWrapper = ref('')

const displayCurrency = computed(() => {
	return !props.selectedOption || !dropdownOptions.value.find(option => option.id === props.selectedOption) ? '' : dropdownOptions.value.find(option => option.id === props.selectedOption).currency
})

const displayDescription = computed(() => {
	return !props.selectedOption || !dropdownOptions.value.find(option => option.id === props.selectedOption) ? '' : dropdownOptions.value.find(option => option.id === props.selectedOption).description
})

const selectedCurrency = computed(() => {
	return props.selectedOption ? dropdownOptions.value.find(option => option.id === props.selectedOption) : null
})

/* Computed for icon */
const iconComponent = computed(() => {
	const iconPath = selectedCurrency.value.icon
	return defineAsyncComponent(() => import('@/assets/icons/' + iconPath + '.svg?inline'))
})

const closeDropdown = () => {
	dropdownIsOpen.value = false
	dropdownBaseWrapper.value.clearSearchValue()
}

/* Watcher for close dropdown when the selectedOption is empty */
watch(() => props.selectedOption, () => {
    if (props.selectedOption === '') dropdownIsOpen.value = false
})

watch(() => dropdownIsOpen.value, () => {
	dropdownOptions.value = props.options
})
</script>

<style lang="scss" scoped>
.header {
    display: flex;
    align-items: center;

	width: 100%;
	height: 100%;

	padding: 0 34px 0 16px;
    border: 1px solid var(--secondary-light-1);
    border-radius: 8px;

    background-color: var(--secondary-light-4);

    .header-content {
        display: flex;
        align-items: flex-end;
        gap: 12px;

        .icon {
            flex-shrink: 0;

            width: 16px;
            height: 16px;
        }

        .placeholder,
        .value {
            @include H6-Regular;

            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .placeholder {
            color: var(--secondary);
        }

        .value {
            color: var(--secondary-dark-3);
        }
    }

    &.cursor-pointer {
		cursor: pointer;
	}

    &.error-state {
		border: 1px solid var(--negative);
	}

    &.active {
        border: 1px solid var(--primary);
    }

    @media (hover: hover) and (pointer: fine) {
        &:hover {
            border: 1px solid var(--secondary-dark-3);
        }
    }
}

.empty-state {
    padding: 0 16px;

    @include H7-Regular;
    line-height: 16px;

    color: var(--secondary-dark-1);
}
</style>
